import React from 'react';
import type { EventCell } from '@/lib/eventWindow';
import { cn } from '@/lib/utils';

/** Visual metric for a reaction-grid cell. Tinting always tracks the
 *  daily return regardless of mode so the visual signal stays consistent
 *  across modes (price mode would otherwise be a sea of grey). */
export type Metric = 'daily' | 'drift' | 'price';

/** Cap the color saturation at ±SATURATION_PCT so a single outlier doesn't
 *  wash out the rest of the grid. Anything beyond renders at full intensity. */
const SATURATION_PCT = 10;

function cellStyle(pct: number): React.CSSProperties {
  const intensity = Math.min(Math.abs(pct) / SATURATION_PCT, 1);
  // Floor at 0.08 so even a tiny move is visible as a tinted cell rather
  // than disappearing into the background.
  const alpha = Math.max(intensity * 0.85, 0.08);
  const rgb = pct >= 0 ? '34, 197, 94' : '239, 68, 68'; // emerald-500 / red-500
  return { backgroundColor: `rgba(${rgb}, ${alpha})` };
}

function metricView(cell: EventCell, metric: Metric): { tintPct: number; label: string; tooltip: string } {
  const tintPct = cell.dailyPct; // visual signal = daily return regardless of mode
  const sign = (n: number) => (n >= 0 ? '+' : '');
  switch (metric) {
    case 'daily': {
      const v = cell.dailyPct;
      return {
        tintPct,
        label: `${sign(v)}${v.toFixed(0)}`,
        tooltip: `${sign(v)}${v.toFixed(1)}%  (daily)`,
      };
    }
    case 'drift': {
      const v = cell.driftPct;
      return {
        tintPct,
        label: `${sign(v)}${v.toFixed(0)}`,
        tooltip: `${sign(v)}${v.toFixed(1)}%  (drift since D0)`,
      };
    }
    case 'price': {
      const label = cell.price >= 10 ? cell.price.toFixed(0) : cell.price.toFixed(2);
      return {
        tintPct,
        label,
        tooltip: `$${cell.price.toFixed(2)}`,
      };
    }
  }
}

/** A single grid cell — tinted by the daily return, labeled by the active
 *  metric. The D0 cell gets the amber star treatment. `d0Label` (default
 *  "Event day") tunes the tooltip — earnings callers pass "Earnings day". */
export function CellBox({
  cell, metric, d0Label = 'Event day',
}: { cell: EventCell | null; metric: Metric; d0Label?: string }) {
  if (!cell) {
    return <div className="w-10 h-6 rounded border border-transparent bg-surface/40" aria-hidden="true" />;
  }
  const view = metricView(cell, metric);
  const isPos = view.tintPct >= 0;
  if (cell.isEventDay) {
    const d0Color = isPos ? 'text-emerald-300 font-semibold' : 'text-red-300 font-semibold';
    return (
      <div
        className={cn(
          'w-10 h-6 rounded border border-amber-400/60 bg-amber-400/10 flex items-center justify-center text-[10px] font-mono tabular-nums',
          d0Color,
        )}
        title={`${d0Label} · D0 · ${cell.date} · ${view.tooltip}`}
      >
        {view.label}
      </div>
    );
  }
  const colorClass = isPos ? 'text-emerald-200' : 'text-red-200';
  return (
    <div
      className={cn('w-10 h-6 rounded border border-default/60 flex items-center justify-center text-[10px] font-mono tabular-nums', colorClass)}
      style={cellStyle(view.tintPct)}
      title={`D${cell.offset >= 0 ? '+' : ''}${cell.offset} · ${cell.date} · ${view.tooltip}`}
    >
      {view.label}
    </div>
  );
}

/** Tri-state EOD%/Drift%/Price segmented control. Caller owns metric state. */
export function MetricToggle({ metric, onChange }: { metric: Metric; onChange: (m: Metric) => void }) {
  const options: Array<{ id: Metric; label: string; title: string }> = [
    { id: 'daily', label: 'EOD %', title: 'Daily close-to-close % change' },
    { id: 'drift', label: 'Drift %', title: 'Cumulative % change since the event-day close' },
    { id: 'price', label: 'Price', title: 'Closing price (USD)' },
  ];
  return (
    <div className="flex rounded-md border border-default overflow-hidden text-[10px]">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          title={o.title}
          onClick={() => onChange(o.id)}
          className={cn(
            'px-2 py-0.5 transition-colors',
            metric === o.id
              ? 'bg-sky-600/80 text-white'
              : 'text-fg-muted hover:text-slate-200 bg-surface/40',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Top-of-grid trading-day offset strip. Renders blanks on the left for
 *  the row-label column (`labelWidthClass`), then -before…+after cells
 *  with an amber star at D0 (no "D0" text — the star marks it). */
export function ReactionHeader({
  before, after, labelWidthClass = 'w-24', d0Tooltip = 'Event day',
}: { before: number; after: number; labelWidthClass?: string; d0Tooltip?: string }) {
  return (
    <div className="flex gap-1">
      <div className={cn(labelWidthClass, 'flex-shrink-0')} />
      {Array.from({ length: after + before + 1 }, (_, idx) => {
        const offset = idx - before;
        const isD0 = offset === 0;
        return (
          <div
            key={idx}
            className={cn(
              'w-10 text-[10px] text-center font-mono leading-tight',
              isD0 ? 'text-amber-300' : 'text-fg-subtle',
            )}
            title={isD0 ? d0Tooltip : undefined}
          >
            {isD0 ? <span aria-hidden="true" className="text-[12px]">★</span> : `${offset > 0 ? '+' : ''}${offset}`}
          </div>
        );
      })}
    </div>
  );
}
